import { useCallback, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import {
  type CollegeVisitTip,
  PRO_TIP_IMAGES,
} from '@backrs_ui/grow-ui/src/pages/CollegeVisitGuide/content';

import { Box, Chip, IconButton, SxProps, Theme, Typography } from '@mui/material';

type ProTipsCarouselProps = Readonly<{
  id: string;
  tips: readonly CollegeVisitTip[];
}>;

export const ProTipsCarousel = ({ id, tips }: ProTipsCarouselProps) => {
  const [index, setIndex] = useState(0);
  const tip = tips[index];
  const image = PRO_TIP_IMAGES[tip.id];

  const goTo = useCallback(
    (next: number) => setIndex((next + tips.length) % tips.length),
    [tips.length],
  );

  return (
    <Box id={id} sx={cardSx}>
      <Box sx={imageSlotSx}>
        {image ? (
          <Box component="img" src={image} alt="" sx={imageSx} />
        ) : (
          <AppIcon iconName="Image01" diameter="32px" color="Colors/Foreground/fg-quaternary" />
        )}
      </Box>
      <Box sx={contentSx}>
        <Chip label={tip.source} variant="badge" size="sm" type="default" sx={chipSx} />
        <Typography variant="text-lg-semibold" sx={titleSx}>
          {tip.title}
        </Typography>
        <Typography variant="text-md-regular" sx={bodySx}>
          {tip.body}
        </Typography>
        <Box sx={controlsSx}>
          <Box sx={dotsSx}>
            {tips.map((item, i) => (
              <Box
                key={item.id}
                id={`${id}-dot-${item.id}`}
                component="button"
                type="button"
                aria-label={`Show tip ${item.number}`}
                aria-current={i === index}
                onClick={() => goTo(i)}
                sx={dotSx(i === index)}
              />
            ))}
          </Box>
          <Box sx={arrowsSx}>
            <IconButton
              id={`${id}-prev-button`}
              aria-label="Previous tip"
              onClick={() => goTo(index - 1)}
              sx={arrowButtonSx}
            >
              <AppIcon
                iconName="ChevronLeft"
                diameter="20px"
                color="Colors/Foreground/fg-primary"
              />
            </IconButton>
            <IconButton
              id={`${id}-next-button`}
              aria-label="Next tip"
              onClick={() => goTo(index + 1)}
              sx={arrowButtonSx}
            >
              <AppIcon
                iconName="ChevronRight"
                diameter="20px"
                color="Colors/Foreground/fg-primary"
              />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  overflow: 'hidden',
  '@container (min-width: 700px)': {
    flexDirection: 'row',
  },
});

const imageSlotSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  '@container (min-width: 700px)': {
    flex: '0 0 40%',
    minHeight: 'auto',
    borderBottom: 'none',
    borderRight: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  },
});

const imageSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  p: backrs.spacing['spacing-3xl'],
  flex: 1,
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Brand/200']}`,
  backgroundColor: backrs.colors['Brand/50'],
  color: backrs.colors['Brand/700'],
  cursor: 'default',
  '&:hover': {
    backgroundColor: backrs.colors['Brand/50'],
  },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  flex: 1,
});

const controlsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  pt: backrs.spacing['spacing-lg'],
});

const dotsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-sm'],
});

const dotSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '8px',
    height: '8px',
    p: 0,
    boxSizing: 'border-box',
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    borderRadius: backrs.radius['radius-full'],
    cursor: 'pointer',
    backgroundColor: isActive
      ? backrs.colors['Colors/Foreground/fg-brand-primary']
      : backrs.colors['Colors/Border/border-secondary'],
  });

const arrowsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-sm'],
});

const arrowButtonSx: SxProps<Theme> = ({ backrs }) => ({
  p: backrs.spacing['spacing-sm'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-full'],
});
